import { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { toast } from "@/lib/toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAdminFeedbackMetrics } from "@/hooks/useAdminFeedback";
import type {
  SignalCount,
  WeeklyAccumulationPoint,
  ConceptFeedbackRow,
} from "@/hooks/useAdminFeedback";
import { relativeTime } from "@/components/admin/UserDetailDrawer";
import { ConfirmResetAllFeedbackModal } from "@/components/admin/ConfirmResetAllFeedbackModal";

// Mirrors REVIEW_SIGNAL_TYPES in backend `services/milestone_service.py`.
// Hand-synced — no shared source of truth across languages. If the backend
// constant changes, grep for this set and update.
const HIGH_CONFIDENCE_SIGNAL_TYPES = new Set([
  "thumbs_up",
  "thumbs_down",
  "score_override",
  "promote",
  "delete",
]);

function formatWeekLabel(iso: string): string {
  // Pinned to UTC so Monday-UTC bucket starts render as Mondays regardless of
  // the viewer's timezone (Postgres date_trunc emits UTC-aware timestamps).
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

interface Props {
  days: number;
}

export function FeedbackTab({ days }: Props) {
  const [resetAllOpen, setResetAllOpen] = useState(false);
  const { data, isLoading, error } = useAdminFeedbackMetrics(days);

  if (isLoading) {
    return <div className="p-6 text-sm text-slate-500">Loading feedback metrics…</div>;
  }
  if (error || !data) {
    return (
      <Card>
        <CardHeader><CardTitle>Feedback metrics</CardTitle></CardHeader>
        <CardContent>Failed to load feedback metrics.</CardContent>
      </Card>
    );
  }

  const periodTotal = data.signal_distribution.reduce((n, r) => n + r.count, 0);
  const allTimeZero = periodTotal === 0 && data.high_confidence_count === 0;

  if (allTimeZero) {
    return (
      <Card>
        <CardHeader><CardTitle>Feedback metrics</CardTitle></CardHeader>
        <CardContent>No feedback events yet.</CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="feedback-tab">
      <FineTuningReadinessHero
        count={data.high_confidence_count}
        target={data.high_confidence_target}
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <SignalDistributionChart
          data={data.signal_distribution}
          periodDays={data.period_days}
        />
        <WeeklyAccumulationChart data={data.weekly_accumulation} />
      </div>
      <ConceptBreakdownTable rows={data.by_concept} periodDays={data.period_days} />

      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="text-destructive">Danger zone</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Permanently delete every feedback_event row for every user. Cannot be undone.
          </p>
          <Button
            variant="destructive"
            onClick={() => setResetAllOpen(true)}
          >
            Reset all feedback events
          </Button>
        </CardContent>
      </Card>

      <ConfirmResetAllFeedbackModal
        open={resetAllOpen}
        onClose={() => setResetAllOpen(false)}
        onReset={(deleted) => {
          toast.success(
            `Deleted ${deleted} feedback event${deleted === 1 ? "" : "s"}.`
          );
          setResetAllOpen(false);
        }}
      />
    </div>
  );
}

function FineTuningReadinessHero({ count, target }: { count: number; target: number }) {
  const pct = Math.min(100, Math.round((count / target) * 100));
  const barColor =
    count >= target ? "bg-emerald-500"
    : count >= target / 2 ? "bg-amber-500"
    : "bg-slate-400";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fine-tuning readiness</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-semibold tabular-nums">{count}</span>
          <span className="text-xl text-slate-500 tabular-nums">/ {target}</span>
        </div>
        <div className="mt-3 h-2 w-full rounded-full bg-slate-100">
          <div
            className={`h-2 rounded-full ${barColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="mt-2 text-sm text-slate-600">
          {count} high-confidence triples across all time. Target: {target} for first fine-tune.
        </p>
      </CardContent>
    </Card>
  );
}

function SignalDistributionChart({
  data,
  periodDays,
}: {
  data: SignalCount[];
  periodDays: number;
}) {
  if (data.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle>Signal distribution</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-500">
          No events in the last {periodDays} days.
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader><CardTitle>Signal distribution</CardTitle></CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} layout="vertical" margin={{ left: 80 }}>
            <XAxis type="number" />
            <YAxis type="category" dataKey="signal_type" width={120} />
            <Tooltip />
            <Bar dataKey="count">
              {data.map((row) => (
                <Cell
                  key={row.signal_type}
                  fill={
                    HIGH_CONFIDENCE_SIGNAL_TYPES.has(row.signal_type)
                      ? "#10b981" // emerald-500
                      : "#94a3b8" // slate-400
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function WeeklyAccumulationChart({ data }: { data: WeeklyAccumulationPoint[] }) {
  const chartData = data.map((p) => ({
    ...p,
    label: formatWeekLabel(p.week_start),
  }));
  return (
    <Card>
      <CardHeader><CardTitle>Weekly high-confidence signals</CardTitle></CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData}>
            <XAxis dataKey="label" />
            <YAxis allowDecimals={false} />
            <Tooltip
              labelFormatter={(label, payload) => {
                const iso = payload?.[0]?.payload?.week_start;
                if (!iso) return label;
                const start = new Date(iso);
                const end = new Date(start);
                end.setDate(end.getDate() + 6);
                return `${formatWeekLabel(iso)} – ${formatWeekLabel(end.toISOString())}`;
              }}
            />
            <Bar dataKey="count" fill="#10b981" />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

type SortKey = "total" | "high_confidence" | "last_at";

function ConceptBreakdownTable({
  rows,
  periodDays,
}: {
  rows: ConceptFeedbackRow[];
  periodDays: number;
}) {
  const [sortKey, setSortKey] = useState<SortKey>("total");
  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      if (sortKey === "last_at") {
        return new Date(b.last_at).getTime() - new Date(a.last_at).getTime();
      }
      return b[sortKey] - a[sortKey];
    });
    return copy;
  }, [rows, sortKey]);

  if (rows.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle>By concept</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-500">
          No events in the last {periodDays} days.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader><CardTitle>By concept</CardTitle></CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
              <th scope="col" className="py-2">Concept</th>
              <th scope="col" className="py-2">Owner</th>
              <th
                scope="col"
                className="px-3 py-2 text-center"
                aria-sort={sortKey === "total" ? "descending" : "none"}
              >
                <button
                  type="button"
                  className="w-full cursor-pointer select-none text-center uppercase tracking-wide"
                  onClick={() => setSortKey("total")}
                >
                  Total {sortKey === "total" && "↓"}
                </button>
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-center"
                aria-sort={sortKey === "high_confidence" ? "descending" : "none"}
              >
                <button
                  type="button"
                  className="w-full cursor-pointer select-none text-center uppercase tracking-wide"
                  onClick={() => setSortKey("high_confidence")}
                >
                  High-conf {sortKey === "high_confidence" && "↓"}
                </button>
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-center"
                aria-sort={sortKey === "last_at" ? "descending" : "none"}
              >
                <button
                  type="button"
                  className="w-full cursor-pointer select-none text-center uppercase tracking-wide"
                  onClick={() => setSortKey("last_at")}
                >
                  Last signal {sortKey === "last_at" && "↓"}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr key={row.concept_id} className="border-b">
                <td className="py-2 pr-3">{row.name}</td>
                <td className="py-2 pr-3 text-slate-600">{row.owner_email}</td>
                <td className="px-3 py-2 text-center tabular-nums">{row.total}</td>
                <td className="px-3 py-2 text-center tabular-nums">{row.high_confidence}</td>
                <td className="px-3 py-2 text-center">{relativeTime(row.last_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 50 && (
          <p className="mt-2 text-xs text-slate-500">Showing top 50 by total.</p>
        )}
      </CardContent>
    </Card>
  );
}
